import { useCoreServices } from '@bangle.io/context';
import {
  ensureFreshSession,
  isSupabaseConfigured,
  onAuthStateChange,
} from '@bangle.io/supabase';
import { useStore } from 'jotai';
import React, { useEffect, useState } from 'react';
import {
  consumePendingOpenWorkspace,
  removeLocalCloudWorkspaces,
  syncCloudWorkspacesLocally,
} from './sync-cloud-workspaces';
import { useCloudWorkspaceAccess } from './use-cloud-workspace-access';
import { WorkspacePresenceController } from './workspace-presence-controller';

type AuthStatus = 'loading' | 'signed-in' | 'signed-out';

/**
 * Side effects for cloud auth / workspace sync, plus the view-only banner.
 * Account chrome (save status, email, share) lives in {@link CloudToolbar}
 * inside the app header so it cannot cover titlebar controls.
 */
export function CloudChrome() {
  const core = useCoreServices();
  const store = useStore();
  const [authStatus, setAuthStatus] = useState<AuthStatus>('loading');
  const { isViewer } = useCloudWorkspaceAccess();

  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    let cancelled = false;
    void ensureFreshSession().then((s) => {
      if (cancelled) return;
      setAuthStatus(s?.user ? 'signed-in' : 'signed-out');
    });
    const unsub = onAuthStateChange((s) => {
      if (cancelled) return;
      setAuthStatus(s?.user ? 'signed-in' : 'signed-out');
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured() || authStatus === 'loading') return;

    if (authStatus === 'signed-out') {
      void removeLocalCloudWorkspaces(core.workspaceOps)
        .then((removed) => {
          if (removed.length === 0) return;
          const currentWsName = store.get(core.navigation.$wsName);
          if (currentWsName && removed.includes(currentWsName)) {
            core.navigation.goHome();
          }
        })
        .catch((err) => {
          console.warn('cloud workspace cleanup failed', err);
        });
      return;
    }

    void syncCloudWorkspacesLocally(core.workspaceOps, { authVerified: true })
      .then(({ nameById, renames }) => {
        const pendingWorkspaceId = consumePendingOpenWorkspace();
        if (pendingWorkspaceId) {
          const wsName = nameById.get(pendingWorkspaceId);
          if (wsName) {
            core.navigation.goWorkspace(wsName);
            return;
          }
        }

        // Follow local renames that restore spaces in cloud titles.
        const currentWsName = store.get(core.navigation.$wsName);
        if (!currentWsName) return;
        const renamedTo = renames.get(currentWsName);
        if (!renamedTo) return;

        const currentRoute = store.get(core.navigation.$routeInfo);
        if (currentRoute.route === 'editor') {
          const wsPath = currentRoute.payload?.wsPath;
          if (
            typeof wsPath === 'string' &&
            wsPath.startsWith(`${currentWsName}:`)
          ) {
            core.navigation.goWsFile(
              `${renamedTo}${wsPath.slice(currentWsName.length)}`,
            );
            return;
          }
        }
        core.navigation.goWorkspace(renamedTo);
      })
      .catch((err) => {
        console.warn('cloud workspace sync failed', err);
        // Auth death clears the store inside listMyWorkspaces; mirror that in UI.
        void ensureFreshSession().then((s) => {
          setAuthStatus(s?.user ? 'signed-in' : 'signed-out');
        });
      });
  }, [authStatus, core.navigation, core.workspaceOps, store]);

  if (!isSupabaseConfigured()) {
    return null;
  }

  return (
    <>
      <WorkspacePresenceController />
      {isViewer ? (
        <div className="sn-viewer-banner">
          View-only — you cannot edit this shared notebook
        </div>
      ) : null}
    </>
  );
}
